import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { seoAPI, clientsAPI } from '../../services/api';
import { Card, Button } from '../ui';
import { handleApiError, showToast, showConfirm, getImageUrl } from '../../utils/helpers';
import { ChevronLeft, Table2, Search, Link2, Pencil, Send, Plus, Info, FileDown, FileUp, ChevronDown } from 'lucide-react';
import SeoKeywordEditModal from '../seo/SeoKeywordEditModal';
import SeoAddKeywordModal from '../seo/SeoAddKeywordModal';
import SeoPostPreviewModal from '../seo/SeoPostPreviewModal';
import SeoPlanFormModal from '../seo/SeoPlanFormModal';
import SeoImportModal from '../seo/SeoImportModal';
import MediaLibraryPickerModal from '../media/MediaLibraryPickerModal';

const ClientSeoSection = ({
  clientId,
  clientName,
  readOnly = false,
  showHeader = true,
  selectedPlanId: controlledPlanId,
  onPlanSelect,
}) => {
  const { isRTL, t } = useLanguage();
  const { isAdmin, isSeoSpecialist } = useAuth();
  const isControlled = typeof onPlanSelect === 'function';

  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [internalPlanId, setInternalPlanId] = useState(null);
  const [viewMode, setViewMode] = useState('list');

  const effectivePlanId = isControlled ? controlledPlanId : internalPlanId;
  const effectiveView = isControlled ? (controlledPlanId ? 'plan' : 'list') : viewMode;

  const [keywords, setKeywords] = useState([]);
  const [keywordsLoading, setKeywordsLoading] = useState(false);
  const [generatingLink, setGeneratingLink] = useState(false);

  const [showPlanModal, setShowPlanModal] = useState(false);
  const [planForModal, setPlanForModal] = useState(null); // null = create, plan = view details

  const [editingKeyword, setEditingKeyword] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCell, setEditingCell] = useState(null); // { keywordId, field }
  const [savingCellId, setSavingCellId] = useState(null);
  const [clientPosts, setClientPosts] = useState([]);
  const [updatingStatusId, setUpdatingStatusId] = useState(null);
  const [previewPostId, setPreviewPostId] = useState(null);
  const [hoverPreviewPostId, setHoverPreviewPostId] = useState(null);
  const [mediaPicker, setMediaPicker] = useState(null); // { keywordId, field: 'design_media_id_1' | 'design_media_id_2' }
  const [showImportModal, setShowImportModal] = useState(false);
  const [importing, setImporting] = useState(false);
  const [addingInline, setAddingInline] = useState(false);
  const addRowInputRef = useRef(null);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const actionsRef = useRef(null);
  const moreBtnRef = useRef(null);
  const dropdownRef = useRef(null);

  const canManageSeo = isAdmin || isSeoSpecialist;
  const canEdit = canManageSeo && !readOnly;

  useEffect(() => {
    if (!clientId || !canManageSeo) return;
    fetchPlans();
  }, [clientId, canManageSeo]);

  const fetchPlans = async () => {
    try {
      setPlansLoading(true);
      const res = await seoAPI.getPlansByClient(clientId);
      const list = res.data.plans || [];
      setPlans(list);
      if (!isControlled) {
        if (list.length > 0 && !internalPlanId) setInternalPlanId(list[0].id);
        if (list.length === 0) {
          setInternalPlanId(null);
          setViewMode('list');
        }
      }
    } catch (err) {
      showToast.error(handleApiError(err));
    } finally {
      setPlansLoading(false);
    }
  };

  useEffect(() => {
    if (!effectivePlanId) {
      setKeywords([]);
      return;
    }
    if (effectiveView === 'plan') {
      fetchKeywords(effectivePlanId);
    }
  }, [effectivePlanId, effectiveView]);

  useEffect(() => {
    if (clientId && effectiveView === 'plan') {
      clientsAPI.getPosts(clientId).then((res) => {
        setClientPosts(res.data.posts || []);
      }).catch(() => setClientPosts([]));
    }
  }, [clientId, effectiveView]);

  useEffect(() => {
    if (!actionsOpen) return;
    const btn = moreBtnRef.current;
    if (btn) {
      const rect = btn.getBoundingClientRect();
      const menuWidth = 200;
      const gap = 4;
      let left = rect.left;
      if (isRTL) left = rect.right - menuWidth;
      const maxLeft = window.innerWidth - menuWidth - 12;
      if (left > maxLeft) left = maxLeft;
      if (left < 12) left = 12;
      setDropdownPos({
        top: rect.bottom + gap,
        left,
      });
    }
    const onOutside = (e) => {
      const insideActions = actionsRef.current?.contains(e.target);
      const insideDropdown = dropdownRef.current?.contains(e.target);
      if (!insideActions && !insideDropdown) setActionsOpen(false);
    };
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, [actionsOpen, isRTL]);

  const fetchKeywords = async (planId) => {
    try {
      setKeywordsLoading(true);
      const res = await seoAPI.getKeywordsByPlan(planId);
      setKeywords(res.data.keywords || []);
    } catch (err) {
      showToast.error(handleApiError(err));
    } finally {
      setKeywordsLoading(false);
    }
  };

  const handlePlanCreated = (plan) => {
    fetchPlans();
    if (plan?.id) {
      if (isControlled) onPlanSelect(plan.id, plan);
      else {
        setInternalPlanId(plan.id);
        setViewMode('plan');
      }
    }
  };

  const handleInlineSave = async (keywordId, field, value) => {
    try {
      setSavingCellId(keywordId);
      const payload = { [field]: value === '' || value === null ? null : value };
      await seoAPI.updateKeyword(keywordId, payload);
      await fetchKeywords(effectivePlanId);
      setEditingCell(null);
    } catch (err) {
      showToast.error(handleApiError(err));
    } finally {
      setSavingCellId(null);
    }
  };

  const handleSendToClient = async (keywordId) => {
    try {
      setUpdatingStatusId(keywordId);
      await seoAPI.updateKeyword(keywordId, { status: 'proposed' });
      showToast.success(isRTL ? 'تم إرسال الكلمة للعميل' : 'Keyword sent to client');
      await fetchKeywords(effectivePlanId);
    } catch (err) {
      showToast.error(handleApiError(err));
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const handleKeywordSaved = () => {
    fetchKeywords(effectivePlanId);
  };

  const getPostTitle = (postId) => {
    const p = clientPosts.find((x) => x.id === postId);
    if (!p) return '-';
    if (p.title && p.title.trim()) return p.title.trim();
    if (p.serial_number) return `#${p.serial_number}`;
    return p.id?.slice(0, 8) || '-';
  };

  const getPostById = (postId) => clientPosts.find((x) => x.id === postId);

  const handleCopyOrGenerateLink = async () => {
    if (!effectivePlanId) return;
    const plan = plans.find((p) => p.id === effectivePlanId);
    const token = plan?.share_token;
    if (token) {
      const url = `${window.location.origin}/seo/public/${token}`;
      await navigator.clipboard.writeText(url);
      showToast.success(t('seoLinkCopied'));
      return;
    }
    try {
      setGeneratingLink(true);
      const res = await seoAPI.generateShareLink(effectivePlanId);
      const newToken = res.data?.share_token;
      if (newToken) {
        const url = `${window.location.origin}/seo/public/${newToken}`;
        await navigator.clipboard.writeText(url);
        showToast.success(t('seoLinkCopied'));
        await fetchPlans();
      }
    } catch (err) {
      showToast.error(handleApiError(err));
    } finally {
      setGeneratingLink(false);
    }
  };

  const handleExportKeywords = () => {
    if (!keywords.length) {
      showToast.error(isRTL ? 'لا توجد كلمات للتصدير' : 'No keywords to export');
      return;
    }
    const headers = ['keyword', 'meta_title', 'meta_description', 'status', 'publish', 'backlink_url'];
    const rows = keywords.map((k) =>
      headers.map((h) => {
        const v = k[h];
        if (v === null || v === undefined) return '';
        const s = String(v);
        return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
      }).join(',')
    );
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `seo-keywords-${selectedPlan?.name || 'plan'}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    showToast.success(isRTL ? 'تم تصدير الكلمات' : 'Keywords exported');
  };

  const processImportFile = async (file) => {
    if (!file || !effectivePlanId) return;
    const text = await file.text();
    const lines = text.split(/\r?\n/).filter((l) => l.trim());
    if (lines.length < 2) {
      showToast.error(isRTL ? 'الملف فارغ أو غير صالح' : 'File is empty or invalid');
      return;
    }
    const header = lines[0].toLowerCase();
    const cols = header.split(',').map((c) => c.trim().replace(/^"|"$/g, ''));
    const ki = cols.findIndex((c) => c === 'keyword' || c === 'كلمة');
    if (ki < 0) {
      showToast.error(isRTL ? 'العمود keyword مطلوب' : 'keyword column is required');
      return;
    }
    const parse = (line) => {
      const out = [];
      let cur = '';
      let inQ = false;
      for (let i = 0; i < line.length; i++) {
        if (line[i] === '"') { inQ = !inQ; continue; }
        if (!inQ && line[i] === ',') { out.push(cur.trim()); cur = ''; continue; }
        cur += line[i];
      }
      out.push(cur.trim());
      return out;
    };
    let added = 0;
    setImporting(true);
    try {
      for (let i = 1; i < lines.length; i++) {
        const vals = parse(lines[i]);
        const keyword = vals[ki]?.trim();
        if (!keyword) continue;
        const payload = { keyword, status: 'proposed', publish: false };
        const metaTitleIdx = cols.findIndex((c) => c === 'meta_title' || c === 'meta title');
        const metaDescIdx = cols.findIndex((c) => c === 'meta_description' || c === 'meta description');
        if (metaTitleIdx >= 0 && vals[metaTitleIdx]) payload.meta_title = vals[metaTitleIdx].trim();
        if (metaDescIdx >= 0 && vals[metaDescIdx]) payload.meta_description = vals[metaDescIdx].trim();
        await seoAPI.addKeyword(effectivePlanId, payload);
        added++;
      }
      if (added > 0) {
        showToast.success(isRTL ? `تم استيراد ${added} كلمة` : `${added} keywords imported`);
        fetchKeywords(effectivePlanId);
      } else {
        showToast.error(isRTL ? 'لم تتم إضافة أي كلمات' : 'No keywords were added');
      }
    } catch (err) {
      showToast.error(handleApiError(err));
    } finally {
      setImporting(false);
    }
  };

  const handleImportFile = async (file) => {
    await processImportFile(file);
  };

  const handleAddKeywordInline = async (keyword) => {
    const k = String(keyword || '').trim();
    if (!k || !effectivePlanId) return;
    try {
      setAddingInline(true);
      await seoAPI.addKeyword(effectivePlanId, { keyword: k, status: 'proposed', publish: false });
      showToast.success(isRTL ? 'تمت إضافة الكلمة' : 'Keyword added');
      await fetchKeywords(effectivePlanId);
      addRowInputRef.current && (addRowInputRef.current.value = '');
    } catch (err) {
      showToast.error(handleApiError(err));
    } finally {
      setAddingInline(false);
    }
  };

  const openPlanView = (planId) => {
    const plan = plans.find((p) => p.id === planId);
    if (isControlled) onPlanSelect(planId, plan);
    else {
      setInternalPlanId(planId);
      setViewMode('plan');
    }
  };

  const backToList = () => {
    setViewMode('list');
  };

  const selectedPlan = plans.find((p) => p.id === effectivePlanId);
  const hasShareLink = selectedPlan?.share_token;

  if (!clientId || !canManageSeo) return null;

  return (
    <Card className="overflow-hidden" dir={isRTL ? 'rtl' : 'ltr'}>
      {showHeader && (
      <div className="p-5 bg-gradient-to-r from-amber-50 via-white to-sky-50 border-b border-slate-200">
        <div className="flex items-center gap-3">
          <span className="flex w-10 h-10 items-center justify-center rounded-xl bg-amber-100 text-amber-600">
            <Search className="w-5 h-5" />
          </span>
          <div>
            <h3 className="text-lg font-semibold text-slate-900">
              {isRTL ? `خطط SEO - ${clientName || 'العميل'}` : `SEO Plans - ${clientName || 'Client'}`}
            </h3>
            <p className="text-sm text-slate-600">
              {readOnly
                ? (isRTL ? 'للاطلاع والمراجعة فقط' : 'View and review only')
                : (isRTL ? 'إدارة الخطط والكلمات المفتاحية' : 'Manage plans and keywords')}
            </p>
          </div>
        </div>
      </div>
      )}

      <div className="p-6">
        {effectiveView === 'list' && (
          <>
            {plansLoading ? (
              <div className="animate-pulse space-y-4">
                <div className="h-20 bg-slate-100 rounded-xl" />
                <div className="h-20 bg-slate-100 rounded-xl" />
                <div className="h-20 bg-slate-100 rounded-xl" />
              </div>
            ) : plans.length === 0 ? (
              <div className="space-y-4">
                <p className="text-sm text-slate-600">
                  {canEdit ? t('seoNoPlansCreate') : t('seoNoPlans')}
                </p>
                {canEdit && (
                  <Button
                    type="button"
                    onClick={() => { setPlanForModal(null); setShowPlanModal(true); }}
                    className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white"
                  >
                    <Plus className="w-4 h-4" />
                    {isRTL ? 'إنشاء خطة SEO' : 'Create SEO plan'}
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <span className="text-sm text-slate-600">
                    {isRTL ? 'اختر خطة لعرض الكلمات المفتاحية' : 'Select a plan to view keywords'}
                  </span>
                  {canEdit && (
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={() => { setPlanForModal(null); setShowPlanModal(true); }}
                      className="inline-flex items-center gap-1"
                    >
                      <Plus className="w-4 h-4" />
                      {isRTL ? 'إنشاء خطة جديدة' : 'New plan'}
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {plans.map((plan) => (
                    <div
                      key={plan.id}
                      className="flex items-center gap-2 flex-1 min-w-0"
                    >
                      <button
                        type="button"
                        onClick={() => openPlanView(plan.id)}
                        className="flex-1 flex items-center justify-between gap-3 p-4 text-left rounded-xl border-2 border-slate-200 hover:border-amber-400 hover:bg-amber-50/30 transition-all group min-w-0"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-slate-900 truncate">{plan.name}</p>
                          {(plan.month || plan.year) && (
                            <p className="text-xs text-slate-500 mt-0.5">
                              {plan.month ? `${plan.month}/` : ''}
                              {plan.year || ''}
                            </p>
                          )}
                        </div>
                        <Table2 className="w-5 h-5 text-slate-400 group-hover:text-amber-600 flex-shrink-0" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setPlanForModal(plan); setShowPlanModal(true); }}
                        className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 shrink-0"
                        title={isRTL ? 'تفاصيل الخطة' : 'Plan details'}
                      >
                        <Info className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {effectiveView === 'plan' && effectivePlanId && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              {!isControlled && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={backToList}
                className="inline-flex items-center gap-1"
              >
                <ChevronLeft className="w-4 h-4" />
                {t('seoBackToPlans')}
              </Button>
              )}
              <h4 className="text-sm font-semibold text-slate-800">
                {selectedPlan?.name}
                {(selectedPlan?.month || selectedPlan?.year) && (
                  <span className="text-slate-500 font-normal ml-1">
                    ({selectedPlan?.month ? `${selectedPlan.month}/` : ''}
                    {selectedPlan?.year || ''})
                  </span>
                )}
              </h4>
              <span className="text-xs text-slate-500">
                {keywords.length} {t('seoKeywordsCount')}
              </span>
              {canEdit && (
                <div className="flex items-center gap-2 ml-auto" ref={actionsRef}>
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => setShowAddModal(true)}
                    className="inline-flex items-center gap-1 bg-amber-600 hover:bg-amber-700 text-white"
                  >
                    <Plus className="w-4 h-4" />
                    {isRTL ? 'إضافة كلمة' : 'Add keyword'}
                  </Button>
                  <div className="relative" ref={moreBtnRef}>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => setActionsOpen((o) => !o)}
                      className="inline-flex items-center gap-1"
                      aria-haspopup="true"
                      aria-expanded={actionsOpen}
                    >
                      {isRTL ? 'المزيد' : 'More'}
                      <ChevronDown className={`w-4 h-4 transition-transform ${actionsOpen ? 'rotate-180' : ''}`} />
                    </Button>
                    {actionsOpen && createPortal(
                      <div
                        ref={dropdownRef}
                        className="fixed py-2 min-w-[200px] bg-white border border-slate-200 rounded-xl shadow-xl z-[9999]"
                        style={{ top: dropdownPos.top, left: dropdownPos.left }}
                        role="menu"
                        dir={isRTL ? 'rtl' : 'ltr'}
                      >
                        <button
                          type="button"
                          role="menuitem"
                          className="w-full px-4 py-2.5 text-start text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2 disabled:opacity-60 whitespace-nowrap"
                          onClick={() => { handleCopyOrGenerateLink(); setActionsOpen(false); }}
                          disabled={generatingLink}
                        >
                          <Link2 className="w-4 h-4" />
                          {isRTL ? 'نسخ رابط الخطة' : 'Copy plan link'}
                        </button>
                        <div className="border-t border-slate-100 my-1" />
                        <button
                          type="button"
                          role="menuitem"
                          className="w-full px-4 py-2.5 text-start text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2 disabled:opacity-60 whitespace-nowrap"
                          onClick={() => { setShowImportModal(true); setActionsOpen(false); }}
                          disabled={importing}
                        >
                          <FileUp className="w-4 h-4" />
                          {isRTL ? 'استيراد' : 'Import'}
                        </button>
                        <button
                          type="button"
                          role="menuitem"
                          className="w-full px-4 py-2.5 text-start text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2 whitespace-nowrap"
                          onClick={() => { handleExportKeywords(); setActionsOpen(false); }}
                        >
                          <FileDown className="w-4 h-4" />
                          {isRTL ? 'تصدير' : 'Export'}
                        </button>
                      </div>,
                      document.body
                    )}
                  </div>
                </div>
              )}
            </div>
            {canEdit && hasShareLink && (
              <p className="text-xs text-slate-500 flex items-center gap-1">
                <Link2 className="w-3 h-3" />
                {t('seoClientReview')}
              </p>
            )}

            {keywordsLoading ? (
              <div className="animate-pulse space-y-2">
                <div className="h-10 bg-slate-100 rounded" />
                <div className="h-10 bg-slate-100 rounded" />
                <div className="h-10 bg-slate-100 rounded" />
              </div>
            ) : (
              <>
                <div className="overflow-x-auto border border-slate-200 rounded-xl bg-white shadow-sm">
                  <table className="min-w-full text-sm border-collapse">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 whitespace-nowrap">
                          #
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 whitespace-nowrap">
                          {isRTL ? 'الكلمة المفتاحية' : 'Keyword'}
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 whitespace-nowrap">
                          {isRTL ? 'الحالة' : 'Status'}
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 whitespace-nowrap">
                          {isRTL ? 'ينشر' : 'Publish'}
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 whitespace-nowrap">
                          {isRTL ? 'عنوان الميتا' : 'Meta title'}
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 whitespace-nowrap">
                          {isRTL ? 'وصف الميتا' : 'Meta description'}
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 whitespace-nowrap">
                          {isRTL ? 'رابط النشر' : 'Backlink'}
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 whitespace-nowrap">
                          {isRTL ? 'المحتوى' : 'Content'}
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 whitespace-nowrap">
                          {isRTL ? 'التصميم 1' : 'Design 1'}
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 whitespace-nowrap">
                          {isRTL ? 'التصميم 2' : 'Design 2'}
                        </th>
                        {canEdit && (
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 whitespace-nowrap">
                            {isRTL ? 'إجراءات' : 'Actions'}
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {keywords.length === 0 ? (
                        <tr>
                          <td colSpan={canEdit ? 11 : 10} className="px-4 py-8 text-center text-slate-500 text-sm">
                            {canEdit
                              ? (isRTL ? 'لا توجد كلمات بعد. اضغط على زر إضافة كلمة للبدء.' : 'No keywords yet. Click Add keyword to start.')
                              : (isRTL ? 'لا توجد كلمات في هذه الخطة.' : 'No keywords in this plan.')}
                          </td>
                        </tr>
                      ) : (
                        keywords.map((k, idx) => (
                          <tr key={k.id} className="hover:bg-slate-50/80">
                            <td className="px-4 py-2.5 text-slate-500">{idx + 1}</td>
                            <td
                              className={`px-4 py-1 min-w-[120px] max-w-[220px] ${canEdit ? 'cursor-pointer hover:bg-amber-50/50' : ''}`}
                              onClick={canEdit && !editingCell ? () => setEditingCell({ keywordId: k.id, field: 'keyword' }) : undefined}
                            >
                              {canEdit && editingCell?.keywordId === k.id && editingCell?.field === 'keyword' ? (
                                <input
                                  type="text"
                                  defaultValue={k.keyword || ''}
                                  autoFocus
                                  className="w-full border border-amber-300 rounded px-2 py-1 text-xs font-medium focus:outline-none focus:ring-1 focus:ring-amber-500"
                                  placeholder={isRTL ? 'الكلمة المفتاحية' : 'Keyword'}
                                  onBlur={(e) => {
                                    const v = e.target.value.trim();
                                    if (v) handleInlineSave(k.id, 'keyword', v);
                                    setEditingCell(null);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      const v = e.target.value.trim();
                                      if (v) handleInlineSave(k.id, 'keyword', v);
                                      setEditingCell(null);
                                    }
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              ) : (
                                <span className="block truncate text-slate-900 font-medium">{k.keyword}</span>
                              )}
                            </td>
                            <td
                              className={`px-4 py-1 ${canEdit ? 'cursor-pointer hover:bg-amber-50/50' : ''}`}
                              onClick={canEdit && !editingCell ? () => setEditingCell({ keywordId: k.id, field: 'status' }) : undefined}
                            >
                              {canEdit && editingCell?.keywordId === k.id && editingCell?.field === 'status' ? (
                                <select
                                  autoFocus
                                  defaultValue={k.status || 'draft'}
                                  className="w-full min-w-[90px] border border-amber-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-amber-500"
                                  onChange={(e) => handleInlineSave(k.id, 'status', e.target.value)}
                                  onBlur={() => setEditingCell(null)}
                                >
                                  <option value="draft">{isRTL ? 'مسودة' : 'draft'}</option>
                                  <option value="proposed">{isRTL ? 'مقترح' : 'proposed'}</option>
                                  <option value="approved">{isRTL ? 'موافق' : 'approved'}</option>
                                  <option value="rejected">{isRTL ? 'مرفوض' : 'rejected'}</option>
                                </select>
                              ) : (
                                <span
                                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                                    k.status === 'approved'
                                      ? 'bg-emerald-50 text-emerald-700 border border-emerald-100'
                                      : k.status === 'rejected'
                                      ? 'bg-rose-50 text-rose-700 border border-rose-100'
                                      : k.status === 'proposed'
                                      ? 'bg-amber-50 text-amber-700 border border-amber-100'
                                      : 'bg-slate-50 text-slate-700 border border-slate-100'
                                  }`}
                                >
                                  {k.status || 'draft'}
                                </span>
                              )}
                            </td>
                            <td
                              className={`px-4 py-1 ${canEdit ? 'cursor-pointer hover:bg-amber-50/50' : ''}`}
                              onClick={canEdit && !editingCell ? () => setEditingCell({ keywordId: k.id, field: 'publish' }) : undefined}
                            >
                              {canEdit && editingCell?.keywordId === k.id && editingCell?.field === 'publish' ? (
                                <select
                                  autoFocus
                                  defaultValue={k.publish ? 'true' : 'false'}
                                  className="w-full min-w-[70px] border border-amber-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-amber-500"
                                  onChange={(e) => handleInlineSave(k.id, 'publish', e.target.value === 'true')}
                                  onBlur={() => setEditingCell(null)}
                                >
                                  <option value="true">{isRTL ? 'نعم' : 'Yes'}</option>
                                  <option value="false">{isRTL ? 'لا' : 'No'}</option>
                                </select>
                              ) : (
                                <span className="text-slate-700">
                                  {k.publish ? (isRTL ? 'نعم' : 'Yes') : (isRTL ? 'لا' : 'No')}
                                </span>
                              )}
                            </td>
                            <td
                              className={`px-4 py-1 min-w-[140px] max-w-[180px] ${canEdit ? 'cursor-pointer hover:bg-amber-50/50' : ''}`}
                              onClick={canEdit && !editingCell ? () => setEditingCell({ keywordId: k.id, field: 'meta_title' }) : undefined}
                            >
                              {canEdit && editingCell?.keywordId === k.id && editingCell?.field === 'meta_title' ? (
                                <input
                                  type="text"
                                  defaultValue={k.meta_title || ''}
                                  autoFocus
                                  className="w-full border border-amber-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-amber-500"
                                  onBlur={(e) => handleInlineSave(k.id, 'meta_title', e.target.value.trim() || null)}
                                  onKeyDown={(e) => e.key === 'Enter' && e.target.blur()}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              ) : (
                                <span className="block truncate text-slate-700">{k.meta_title || '-'}</span>
                              )}
                            </td>
                            <td
                              className={`px-4 py-1 min-w-[140px] max-w-[180px] ${canEdit ? 'cursor-pointer hover:bg-amber-50/50' : ''}`}
                              onClick={canEdit && !editingCell ? () => setEditingCell({ keywordId: k.id, field: 'meta_description' }) : undefined}
                            >
                              {canEdit && editingCell?.keywordId === k.id && editingCell?.field === 'meta_description' ? (
                                <input
                                  type="text"
                                  defaultValue={k.meta_description || ''}
                                  autoFocus
                                  className="w-full border border-amber-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-amber-500"
                                  onBlur={(e) => handleInlineSave(k.id, 'meta_description', e.target.value.trim() || null)}
                                  onKeyDown={(e) => e.key === 'Enter' && e.target.blur()}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              ) : (
                                <span className="block truncate text-slate-700">{k.meta_description || '-'}</span>
                              )}
                            </td>
                            <td
                              className={`px-4 py-1 min-w-[120px] max-w-[160px] ${canEdit ? 'cursor-pointer hover:bg-amber-50/50' : ''}`}
                              onClick={canEdit && !editingCell ? () => setEditingCell({ keywordId: k.id, field: 'backlink_url' }) : undefined}
                            >
                              {canEdit && editingCell?.keywordId === k.id && editingCell?.field === 'backlink_url' ? (
                                <input
                                  type="url"
                                  defaultValue={k.backlink_url || ''}
                                  autoFocus
                                  className="w-full border border-amber-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-amber-500"
                                  placeholder="https://"
                                  onBlur={(e) => handleInlineSave(k.id, 'backlink_url', e.target.value.trim() || null)}
                                  onKeyDown={(e) => e.key === 'Enter' && e.target.blur()}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              ) : k.backlink_url ? (
                                <a href={k.backlink_url} target="_blank" rel="noopener noreferrer" className="text-sky-600 hover:underline truncate block" onClick={(e) => e.stopPropagation()}>
                                  {k.backlink_url}
                                </a>
                              ) : (
                                <span className="text-slate-400">-</span>
                              )}
                            </td>
                            <td
                              className={`px-4 py-1 min-w-[100px] max-w-[140px] relative ${canEdit ? 'hover:bg-amber-50/50' : ''}`}
                            >
                              {canEdit && editingCell?.keywordId === k.id && editingCell?.field === 'article_post_id' ? (
                                <select
                                  autoFocus
                                  defaultValue={k.article_post_id || ''}
                                  className="w-full border border-amber-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-amber-500"
                                  onChange={(e) => handleInlineSave(k.id, 'article_post_id', e.target.value || null)}
                                  onBlur={() => setEditingCell(null)}
                                >
                                  <option value="">{isRTL ? '-- لا يوجد --' : '-- None --'}</option>
                                  {(clientPosts.filter((p) => p.content_type === 'blog')).map((p) => (
                                    <option key={p.id} value={p.id}>{getPostTitle(p.id)}</option>
                                  ))}
                                </select>
                              ) : k.article_post_id ? (
                                <div className="flex items-center gap-1 group">
                                  <button
                                    type="button"
                                    className="truncate text-sky-600 hover:text-sky-700 hover:underline text-left max-w-[100px]"
                                    onClick={(e) => { e.stopPropagation(); setPreviewPostId(k.article_post_id); }}
                                    onMouseEnter={() => setHoverPreviewPostId(k.article_post_id)}
                                    onMouseLeave={() => setHoverPreviewPostId(null)}
                                    title={isRTL ? 'اضغط للمعاينة' : 'Click to preview'}
                                  >
                                    {getPostTitle(k.article_post_id)}
                                  </button>
                                  {canEdit && (
                                    <button
                                      type="button"
                                      className="p-0.5 rounded text-slate-400 hover:text-slate-600 hover:bg-slate-100 opacity-0 group-hover:opacity-100 transition-opacity"
                                      onClick={(e) => { e.stopPropagation(); setEditingCell({ keywordId: k.id, field: 'article_post_id' }); }}
                                      title={isRTL ? 'تعديل' : 'Edit'}
                                    >
                                      <Pencil className="w-3 h-3" />
                                    </button>
                                  )}
                                  {hoverPreviewPostId === k.article_post_id && (
                                    <div className={`absolute z-50 top-full mt-1 min-w-[180px] max-w-[240px] p-2 rounded-lg bg-white border border-slate-200 shadow-lg ${isRTL ? 'right-0' : 'left-0'}`}>
                                      {(() => {
                                        const p = getPostById(k.article_post_id);
                                        return p ? (
                                          <div>
                                            {p.current_image_url && (
                                              <img src={getImageUrl(p.current_image_url)} alt="" className="w-full h-16 object-cover rounded mb-2" />
                                            )}
                                            <p className="text-xs font-medium text-slate-900 truncate">{getPostTitle(p.id)}</p>
                                            {p.description && <p className="text-[11px] text-slate-500 line-clamp-2 mt-0.5">{p.description}</p>}
                                            <p className="text-[10px] text-sky-600 mt-1">{isRTL ? 'اضغط للمعاينة الكاملة' : 'Click for full preview'}</p>
                                          </div>
                                        ) : null;
                                      })()}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <span
                                  className={canEdit ? 'cursor-pointer text-slate-400 hover:text-slate-600' : ''}
                                  onClick={canEdit ? () => setEditingCell({ keywordId: k.id, field: 'article_post_id' }) : undefined}
                                >
                                  -
                                </span>
                              )}
                            </td>
                            <td
                              className={`px-4 py-1 min-w-[100px] max-w-[140px] relative ${canEdit ? 'cursor-pointer hover:bg-amber-50/50' : ''}`}
                              onClick={canEdit ? () => setMediaPicker({ keywordId: k.id, field: 'design_media_id_1' }) : undefined}
                            >
                              {k.design_media_1 ? (
                                <div className="flex items-center gap-2">
                                  <div className="w-10 h-10 rounded overflow-hidden bg-slate-100 shrink-0">
                                    <img src={getImageUrl(k.design_media_1.file_url)} alt="" className="w-full h-full object-cover" />
                                  </div>
                                  <span className="text-xs text-slate-700 truncate max-w-[80px]" title={k.design_media_1.file_name}>
                                    {k.design_media_1.file_name}
                                  </span>
                                </div>
                              ) : k.design_post_id_1 ? (
                                <span className="text-sky-600 text-xs truncate block" title={getPostTitle(k.design_post_id_1)}>
                                  {getPostTitle(k.design_post_id_1)}
                                </span>
                              ) : (
                                <span className="text-slate-400 text-sm">-</span>
                              )}
                            </td>
                            <td
                              className={`px-4 py-1 min-w-[100px] max-w-[140px] relative ${canEdit ? 'cursor-pointer hover:bg-amber-50/50' : ''}`}
                              onClick={canEdit ? () => setMediaPicker({ keywordId: k.id, field: 'design_media_id_2' }) : undefined}
                            >
                              {k.design_media_2 ? (
                                <div className="flex items-center gap-2">
                                  <div className="w-10 h-10 rounded overflow-hidden bg-slate-100 shrink-0">
                                    <img src={getImageUrl(k.design_media_2.file_url)} alt="" className="w-full h-full object-cover" />
                                  </div>
                                  <span className="text-xs text-slate-700 truncate max-w-[80px]" title={k.design_media_2.file_name}>
                                    {k.design_media_2.file_name}
                                  </span>
                                </div>
                              ) : k.design_post_id_2 ? (
                                <span className="text-sky-600 text-xs truncate block" title={getPostTitle(k.design_post_id_2)}>
                                  {getPostTitle(k.design_post_id_2)}
                                </span>
                              ) : (
                                <span className="text-slate-400 text-sm">-</span>
                              )}
                            </td>
                            {canEdit && (
                              <td className="px-4 py-2.5">
                                <div className="flex items-center gap-1">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setEditingKeyword(k)}
                                    className="p-1.5"
                                    title={isRTL ? 'تعديل' : 'Edit'}
                                  >
                                    <Pencil className="w-3.5 h-3.5" />
                                  </Button>
                                  {(k.status === 'draft' || !k.status) && (
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleSendToClient(k.id)}
                                      loading={updatingStatusId === k.id}
                                      className="p-1.5"
                                      title={isRTL ? 'إرسال للعميل' : 'Send to client'}
                                    >
                                      <Send className="w-3.5 h-3.5" />
                                    </Button>
                                  )}
                                </div>
                              </td>
                            )}
                          </tr>
                        ))
                      )}
                      {canEdit && (
                        <tr className="bg-amber-50/30 border-t-2 border-amber-100">
                          <td className="px-4 py-2 text-slate-500">{keywords.length + 1}</td>
                          <td className="px-4 py-2" colSpan={10}>
                            <input
                              ref={addRowInputRef}
                              type="text"
                              placeholder={isRTL ? 'أدخل الكلمة واضغط Enter للإضافة' : 'Type keyword and press Enter to add'}
                              className="w-full max-w-[280px] border border-amber-300 rounded-lg px-3 py-2 text-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-400"
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  handleAddKeywordInline(e.target.value);
                                }
                              }}
                              disabled={addingInline}
                            />
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {canEdit && (
        <>
          <SeoKeywordEditModal
            keyword={editingKeyword}
            clientId={clientId}
            clientName={clientName}
            isOpen={!!editingKeyword}
            onClose={() => setEditingKeyword(null)}
            onSaved={handleKeywordSaved}
          />
          <SeoAddKeywordModal
            planId={effectivePlanId}
            clientId={clientId}
            clientName={clientName}
            isOpen={showAddModal}
            onClose={() => setShowAddModal(false)}
            onSaved={handleKeywordSaved}
          />
        </>
      )}
      <SeoPostPreviewModal
        postId={previewPostId}
        isOpen={!!previewPostId}
        onClose={() => setPreviewPostId(null)}
      />
      {mediaPicker && (
        <MediaLibraryPickerModal
          clientId={clientId}
          clientName={clientName}
          isOpen={!!mediaPicker}
          onClose={() => setMediaPicker(null)}
          onSelect={(media) => {
            handleInlineSave(mediaPicker.keywordId, mediaPicker.field, media ? media.id : null);
            setMediaPicker(null);
          }}
          imageOnly
        />
      )}
      {showPlanModal && (
        <SeoPlanFormModal
          clientId={clientId}
          clientName={clientName}
          isOpen={showPlanModal}
          onClose={() => { setShowPlanModal(false); setPlanForModal(null); }}
          onSuccess={planForModal ? undefined : handlePlanCreated}
          plan={planForModal}
        />
      )}
      <SeoImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onFileSelect={handleImportFile}
      />
    </Card>
  );
};

export default ClientSeoSection;
